import { RoseLogo } from '@inboundx/shared/ui';
import { Check, type LucideIcon } from 'lucide-react';
import { type ReactNode, useLayoutEffect } from 'react';
import { usePageChromeSetter } from '@/components/layout/pageChrome';
import { PixelField } from './PixelField';

/**
 * Two-panel onboarding frame (IX-3922).
 *
 * Left column holds the Rose wordmark + the interactive form. The right column
 * is an animated `PixelField` behind a floating context card. During setup it
 * can tint to the detected site primary color; otherwise it falls back to Rose
 * blue. It is hidden below `lg` so mobile keeps the form full-width.
 */
interface OnboardingShellProps {
    /**
     * The surface's name, for the browser tab. Required: these screens render
     * outside AppLayout, so nothing else can name them, and a wizard step is not
     * a route — its title can't be derived from the path. Making it a prop is
     * what stops an untitled onboarding screen from compiling.
     */
    title: string;
    /** Content of the side panel (a context card). Hidden under `lg`. */
    aside: ReactNode;
    /** Optional slot at the top-right of the form column (e.g. a step counter). */
    topRight?: ReactNode;
    /** CSS colour the pixel-field eases toward (brand-morph). */
    tint?: string;
    children: ReactNode;
}

export function OnboardingShell({ title, aside, topRight, tint, children }: OnboardingShellProps) {
    // Same channel <Page> uses, so DocumentTitle stays the single writer.
    const setChrome = usePageChromeSetter();
    useLayoutEffect(() => {
        setChrome({ title });
        return () => setChrome(null);
    }, [title, setChrome]);

    return (
        <div className="grid min-h-screen grid-cols-1 bg-card lg:grid-cols-2">
            <div className="flex min-h-screen flex-col overflow-hidden px-6 py-7 sm:px-10 lg:border-r lg:border-border-subtle">
                <div className="flex flex-shrink-0 items-center justify-between">
                    <RoseLogo height={24} color="var(--foreground)" />
                    {topRight}
                </div>
                <div className="flex flex-1 flex-col justify-center">
                    <div className="mx-auto w-full max-w-[420px] py-6">{children}</div>
                </div>
            </div>

            <aside className="relative hidden overflow-hidden bg-accent-foreground lg:flex lg:items-center lg:justify-center">
                <PixelField tint={tint} className="absolute inset-0 size-full" />
                {/* Soft top-corner sheen — a white overlay, not a brand tint. */}
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        backgroundImage:
                            'radial-gradient(120% 120% at 85% 0%, rgba(255,255,255,0.18), rgba(255,255,255,0) 55%)',
                    }}
                />
                <div className="relative z-10 w-full max-w-[460px] px-8">{aside}</div>
            </aside>
        </div>
    );
}

/**
 * A floating white card for the side panel: an icon tile, a title, a supporting
 * line, and a single reassuring stat. Kept as an alternative to the live
 * preview for steps where a "why we ask" note fits better.
 */
interface WhyCardProps {
    icon: LucideIcon;
    title: string;
    description: string;
    stat: string;
}

export function WhyCard({ icon: Icon, title, description, stat }: WhyCardProps) {
    return (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start gap-4">
                <span className="flex size-12 flex-shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                    <Icon className="size-6" />
                </span>
                <div>
                    <div className="text-lg font-bold leading-tight tracking-tight text-foreground">
                        {title}
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {description}
                    </p>
                </div>
            </div>
            <div className="mt-5 flex items-center gap-2 border-t border-border pt-4">
                <Check className="size-4 flex-none text-success" />
                <span className="text-[13px] font-semibold text-foreground">{stat}</span>
            </div>
        </div>
    );
}
